import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Link2, ExternalLink, Trash2, AlertCircle, Loader2 } from "lucide-react";
import { usePortfolio } from "@/hooks/usePortfolio";
import { toast } from "@/hooks/use-toast";

export default function AlunoPortfolio() {
  const navigate = useNavigate();
  const { links, isLoading, deleteLink } = usePortfolio();

  const handleDelete = async (id: string) => {
    try {
      await deleteLink.mutateAsync(id);
      toast({ title: "Link removido" });
    } catch {
      toast({ title: "Erro ao remover", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/experiencia")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground">Portfólio</h1>
          <p className="text-muted-foreground text-sm">Links para seus trabalhos</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-accent/10 rounded-xl p-4 mb-6 border border-accent/20 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-foreground">Portfólio em construção?</p>
          <p className="text-xs text-muted-foreground mt-1">Tudo bem! Adicione o que você tem.</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
        <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/aluno/experiencia/portfolio/novo")}>
          <Plus className="w-5 h-5" />Adicionar link
        </Button>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-3">
        {links.length > 0 ? (
          links.map((item) => (
            <div key={item.id} className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Link2 className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.url}</p>
                  <p className="text-xs text-muted-foreground">{item.platform}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon-sm" asChild>
                    <a href={item.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a>
                  </Button>
                  <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-muted/50 rounded-2xl p-8 text-center">
            <Link2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Nenhum link adicionado ainda</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
